import type { SeatedPassengers } from "@b2bportal/air-booking-api";
import {
  DisruptionFailureEnum,
  type PollFulfillSuccess,
  type CipherText,
  type DisruptionFailure,
  type FlightItinerarySegment,
  type OrderedFlight,
  type RebookPollPQSuccess,
  type Route,
} from "@b2bportal/air-disruption-api";
import type { SliceInfo } from "@b2bportal/air-rebook-api";
import {
  CallState,
  mapExternalResource,
  type ExternalResource,
} from "@b2bportal/core-types";
import {
  ensureExhaustive,
  handleAsyncThunk,
  type RichError,
  RichErrors,
  ThunkErrorEnum,
} from "@b2bportal/core-utilities";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { NumericDictionary } from "lodash";
import {
  fetchRebookFlights,
  performPriceQuote,
  performRebookFulfill,
} from "./thunks";

export interface SelectedSegmentState {
  selection: { slice: number; segment: number };
  segment: FlightItinerarySegment;
  route: Route;
}

export interface ShoppingState {
  returnSlice?: SliceInfo;
  passengers: SeatedPassengers;
}

export interface RebookRequest {
  contractId: string;
  fareId: string;
  tripId: string;
}
export interface FlightsRebookState {
  selectedSegment?: SelectedSegmentState;
  shoppingState: ExternalResource<ShoppingState, string>;
  bookingState?:
    | {
        state: "pending";
        request: RebookRequest;
      }
    | {
        state: "price-quote";
        start: number; // timestamp in ms
        resource: ExternalResource<
          {
            response: RebookPollPQSuccess;
            token: CipherText;
            contractId: string;
          },
          RichError<DisruptionFailure>
        >;
      }
    | {
        state: "fulfill";
        start: number; // timestamp in ms
        resource: ExternalResource<
          PollFulfillSuccess,
          RichError<DisruptionFailure>
        >;
      };
  rebookedFlight?: OrderedFlight;
}

const initialState: FlightsRebookState = Object.freeze({
  shoppingState: { state: CallState.NotCalled },
} satisfies FlightsRebookState);

export const flightsRebookSlice = createSlice({
  name: "flights/rebook",
  initialState: { ...initialState },
  reducers: {
    setSelectedSegment: (
      state,
      action: PayloadAction<SelectedSegmentState | undefined>
    ) => {
      state.selectedSegment = action.payload;
    },

    initiateRebook: (state, action: PayloadAction<RebookRequest>) => {
      state.bookingState = {
        state: "pending",
        request: action.payload,
      };
    },

    dismissRebookError: (state) => {
      state.bookingState = undefined;
    },
  },
  extraReducers: (builder) => {
    handleAsyncThunk(builder, fetchRebookFlights, (state, resource) => {
      state.shoppingState = mapExternalResource(
        resource,
        (data) => ({
          returnSlice: data.returnSliceInfo,
          passengers: data.seatedPassengers,
        }),
        (error) => {
          switch (error.type) {
            case ThunkErrorEnum.Aborted:
            case ThunkErrorEnum.Unknown:
              return error.error.message ?? error.type;
            case ThunkErrorEnum.Rejected:
              return error.error.error;
            default:
              ensureExhaustive(error);
          }
        }
      );
    });

    handleAsyncThunk(builder, performPriceQuote, (state, resource) => {
      state.bookingState = {
        state: "price-quote",
        start:
          state.bookingState?.state === "price-quote"
            ? state.bookingState.start
            : Date.now(),
        resource: mapExternalResource(
          resource,
          (data) => data,
          (error) => {
            switch (error.type) {
              case ThunkErrorEnum.Aborted:
              case ThunkErrorEnum.Unknown:
                return RichErrors.failure({
                  DisruptionFailure: DisruptionFailureEnum.Unknown,
                  message: error.error.message ?? error.type,
                } satisfies DisruptionFailure);
              case ThunkErrorEnum.Rejected:
                return error.error;

              default:
                ensureExhaustive(error);
            }
          }
        ),
      };
    });

    handleAsyncThunk(builder, performRebookFulfill, (state, resource) => {
      state.bookingState = {
        state: "fulfill",
        start:
          state.bookingState?.state === "fulfill"
            ? state.bookingState.start
            : Date.now(),
        resource: mapExternalResource(
          resource,
          (data) => data,
          (error) => {
            switch (error.type) {
              case ThunkErrorEnum.Aborted:
              case ThunkErrorEnum.Unknown:
                return RichErrors.failure({
                  DisruptionFailure: DisruptionFailureEnum.Unknown,
                  message: error.error.message ?? error.type,
                } satisfies DisruptionFailure);
              case ThunkErrorEnum.Rejected:
                return error.error;
              default:
                ensureExhaustive(error);
            }
          }
        ),
      };

      if (resource.state === CallState.Success) {
        state.rebookedFlight = resource.data.rebookedFlight;
      }
    });
  },
});

export const FlightsRebookActions = flightsRebookSlice.actions;

export default flightsRebookSlice.reducer;
